import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, BookOpen, Music, Tv, ShieldCheck, Star } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import SafetyPledgeDialog from "@/components/SafetyPledgeDialog";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES, AMAZON_URL, SAFETY_PRINCIPLES, YOUTUBE_EPISODE } from "@/data/brand";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yChars = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yPluggy = useTransform(scrollYProgress, [0, 1], [0, 150]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center pt-24 overflow-hidden bg-sq-bg">
      {/* faint scene backdrop on the right */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-[62%] -z-10">
        <img src={IMAGES.heroScene} alt="" aria-hidden className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-sq-bg via-sq-bg/40 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 items-center gap-8">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sq-ink text-white text-xs font-bold uppercase tracking-[0.15em]"
          >
            <ShieldCheck size={14} className="text-sq-yellow" /> Safety skills that save lives
          </motion.div>

          <h1 className="font-display text-6xl sm:text-8xl lg:text-[8.5rem] leading-[0.85] tracking-tight mt-5">
            <MaskedLines lines={["THE SAFETY", "QUEST"]} delay={0.15} />
          </h1>

          <Reveal delay={0.6} className="mt-6">
            <p className="font-round text-xl sm:text-2xl text-sq-ink/80 max-w-xl">
              Join <b>Safety Coach Bobby</b> and his sidekick <b>Pluggy</b> on a heroic adventure that
              teaches kids fire, road & home safety — through books, music and cartoons.
            </p>
          </Reveal>

          <Reveal delay={0.75} className="mt-8 flex flex-wrap gap-4">
            <a href={AMAZON_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-amazon-cta"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-sq-red text-white font-bold text-lg btn-chunky">
              Buy the Book on Amazon
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <SafetyPledgeDialog trigger={
              <button data-testid="hero-pledge-cta" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-sq-ink font-bold text-lg border-2 border-sq-ink btn-chunky">
                Take the Hero Pledge
              </button>
            } />
          </Reveal>
        </div>

        {/* Characters */}
        <div className="lg:col-span-5 relative hidden lg:block">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[26rem] h-[26rem] rounded-full bg-sq-yellow/70 blur-2xl" />
          <motion.img
            style={{ y: yChars }}
            src={IMAGES.bobby} alt="Firefighter Bobby waving"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.7 }}
            className="relative z-10 mx-auto h-[34rem] object-contain drop-shadow-2xl"
          />
          <motion.img
            style={{ y: yPluggy }}
            src={IMAGES.pluggy} alt="Pluggy the fire hydrant"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="absolute z-20 -right-2 bottom-0 h-48 object-contain drop-shadow-xl"
          />
        </div>
      </div>
    </section>
  );
}

const TEASERS = [
  { icon: BookOpen, title: "Books", desc: "Story-driven adventures starring Bobby & Pluggy. Available now on Amazon.", tag: "Available Now", color: "sq-red", to: "/books" },
  { icon: Tv, title: "Cartoons", desc: "An animated series bringing every safety quest to life on screen.", tag: "Coming Soon", color: "sq-blue", to: "/books" },
  { icon: Music, title: "Music", desc: "Catchy safety sing-along songs kids can't stop singing — now playing on the Books & Media page.", tag: "Now Playing", color: "sq-yellow", to: "/books" },
];

export default function Home() {
  useSEO({
    title: "Children's Safety Books, Cartoons & Music",
    description: "The Safety Quest teaches kids fire safety, seat belt safety, crosswalk safety and emergency preparedness through books, animated cartoons and music with Bobby and Pluggy.",
  });

  return (
    <>
      <Hero />

      {/* Marquee */}
      <div className="bg-sq-ink text-white py-4 border-y-4 border-sq-yellow">
        <Marquee speed={45} gradient={false} autoFill>
          {SAFETY_PRINCIPLES.map((p, i) => (
            <span key={i} className="mx-6 font-display text-2xl tracking-wide flex items-center gap-6">
              {p} <Star size={16} className="text-sq-yellow fill-sq-yellow" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-4">Our Mission</p>
            <h2 className="font-display text-5xl lg:text-7xl leading-[0.9] tracking-tight">
              Turning safety lessons into <span className="text-sq-red">heroic</span> adventures.
            </h2>
            <p className="font-body text-lg text-sq-ink/70 mt-6 max-w-xl leading-relaxed">
              Every year, preventable accidents put children at risk. The Safety Quest empowers kids
              to become confident Safety Heroes — teaching them what to do before an emergency ever
              happens, in a way that feels like play, not a lecture.
            </p>
            <Link to="/about" data-testid="mission-about-link" className="inline-flex items-center gap-2 mt-8 font-bold text-sq-ink hover:gap-3 transition-all">
              Meet the founders <ArrowRight size={18} />
            </Link>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 bg-sq-yellow rounded-[2rem] rotate-3" />
              <img src={IMAGES.toddlerFire} alt="Child in firefighter costume" className="relative rounded-[1.8rem] w-full aspect-[4/5] object-cover" />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border-2 border-sq-ink px-5 py-3 shadow-[0_5px_0_#111827]">
                <p className="font-display text-4xl text-sq-red leading-none">4</p>
                <p className="text-xs font-semibold uppercase tracking-wide">Core Safety Skills</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Featured video */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-red mb-3">Watch now · Episode 1</p>
          <h2 className="font-display text-5xl lg:text-7xl tracking-tight leading-[0.9]">Meet Bobby & Pluggy</h2>
          <p className="font-body text-lg text-sq-ink/70 mt-4 max-w-xl">
            Press play to watch the debut episode of The Safety Quest — join Bobby &amp; Pluggy on
            their very first on-screen adventure.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <YouTubeEmbed id={YOUTUBE_EPISODE.id} title={YOUTUBE_EPISODE.title} />
          </div>
        </Reveal>
      </section>

      {/* Multimedia teaser */}
      <section className="bg-sq-ink text-white py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sq-yellow mb-3">A whole safety universe</p>
            <h2 className="font-display text-5xl lg:text-7xl tracking-tight max-w-2xl leading-[0.9]">One quest. Books, cartoons & music.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {TEASERS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <Link to={t.to} data-testid={`teaser-${t.title.toLowerCase()}`}
                  className="group block h-full rounded-3xl bg-white/[0.04] border border-white/10 p-8 hover:bg-white/[0.08] transition-colors">
                  <div className={`grid place-items-center w-14 h-14 rounded-2xl bg-${t.color} text-white mb-6`}>
                    <t.icon size={26} />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-widest text-white/50">{t.tag}</span>
                  <h3 className="font-round text-3xl font-semibold mt-1">{t.title}</h3>
                  <p className="text-white/60 mt-3 leading-relaxed">{t.desc}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-sq-yellow font-semibold group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={16} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pledge CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-sq-red text-white p-10 lg:p-16">
          <img src={IMAGES.pluggy} alt="Pluggy" className="hidden md:block absolute -right-6 -bottom-10 w-72 drop-shadow-2xl" />
          <div className="relative max-w-xl">
            <h2 className="font-display text-5xl lg:text-7xl tracking-tight leading-[0.9]">Ready to become a Safety Hero?</h2>
            <p className="font-round text-lg mt-4 text-white/90">
              Take the pledge, download your certificate, and join thousands of kids on the quest.
            </p>
            <SafetyPledgeDialog trigger={
              <button data-testid="cta-pledge-btn" className="mt-8 px-8 py-4 rounded-full bg-sq-yellow text-sq-ink font-bold text-lg btn-chunky">
                Take the Hero Pledge
              </button>
            } />
          </div>
        </div>
      </section>
    </>
  );
}
